<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    title="Bulk Order Release Upload"
    subtitle="Upload order releases from Excel, or extract draft release rows from a PDF/image using AI before creating them.">
    <div actions>
      <p-button label="Release Pool" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases"></p-button>
      <p-button label="Download Template" icon="pi pi-download" severity="secondary" [outlined]="true" [loading]="isBusy" (onClick)="downloadTemplate()"></p-button>
    </div>
  </app-page-hero>

  <app-page-data-panel title="Upload Source" subtitle="Preview validates rows before anything is created." [resultText]="previewResultText()">
    @if (message) {
      <div class="notice">{{ message }}</div>
    }

    <div class="reference-note">
      <strong>Use business references, not internal IDs.</strong>
      <span>Upload client numbers, location codes, inventory item numbers, warehouse codes, zone codes, and condition codes instead of database IDs.</span>
    </div>

    <div class="upload-grid">
      <section class="upload-card">
        <div class="card-heading">
          <i class="pi pi-file-excel"></i>
          <div>
            <strong>Excel Workbook</strong>
            <small>Use the template for structured releases and item lines.</small>
          </div>
        </div>
        <input #excelInput class="hidden-file-input" type="file" accept=".xlsx,.xls" (change)="onExcelFileSelected($event)">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-upload"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="excel-file" class="file-display" [value]="excelFileName()" readonly>
            <label for="excel-file">Excel workbook</label>
          </p-iftalabel>
          <p-button label="Browse" icon="pi pi-folder-open" severity="secondary" [outlined]="true" (onClick)="excelInput.click()"></p-button>
        </p-inputgroup>
        <p-button label="Preview Excel" icon="pi pi-search" [disabled]="!excelFile || isBusy" [loading]="isBusy" (onClick)="previewExcel()"></p-button>
      </section>

      <section class="upload-card ai-card">
        <div class="card-heading">
          <i class="pi pi-sparkles"></i>
          <div>
            <strong>AI Document Extraction</strong>
            <small>Extract draft order release rows from PDF or image files.</small>
          </div>
        </div>
        <input #documentInput class="hidden-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" (change)="onDocumentFileSelected($event)">
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-image"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="document-file" class="file-display" [value]="documentFileName()" readonly>
            <label for="document-file">PDF or image</label>
          </p-iftalabel>
          <p-button label="Browse" icon="pi pi-folder-open" severity="secondary" [outlined]="true" (onClick)="documentInput.click()"></p-button>
        </p-inputgroup>
        <p-button label="Extract With AI" icon="pi pi-bolt" severity="secondary" [outlined]="true" [disabled]="!documentFile || isBusy" [loading]="isBusy" (onClick)="extractDocument()"></p-button>
      </section>
    </div>
  </app-page-data-panel>

  <app-page-data-panel title="Preview Rows" subtitle="Only valid rows are sent to release generation." [resultText]="previewResultText()">
    <div class="preview-actions">
      <div>
        <p-tag [value]="(preview?.source || 'NO SOURCE')" severity="secondary"></p-tag>
        @if (preview?.fileName) {
          <span class="file-name">{{ preview?.fileName }}</span>
        }
      </div>
      <p-button
        label="Create Valid Releases"
        icon="pi pi-check"
        [disabled]="validRows().length === 0 || isBusy"
        [loading]="isBusy"
        (onClick)="createValidRows()">
      </p-button>
    </div>

    <p-table [value]="rows()" responsiveLayout="scroll" styleClass="workspace-table bulk-preview-table">
      <ng-template pTemplate="header">
        <tr>
          <th>Status</th>
          <th>Reference</th>
          <th>Customer</th>
          <th>Route</th>
          <th>Need By</th>
          <th>Load / SKU</th>
          <th>Message</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr [class.invalid-row]="row.validationStatus !== 'READY'">
          <td><p-tag [value]="row.validationStatus || 'PENDING'" [severity]="statusSeverity(row.validationStatus)"></p-tag></td>
          <td>
            <strong>{{ row.externalReleaseRef || row.sourceNumber || ('Row ' + row.rowNumber) }}</strong>
            <small>{{ row.customerTrackingNumber || 'No tracking number' }}</small>
          </td>
          <td>
            <strong>{{ customerLabel(row) }}</strong>
            <small>{{ row.sourceNumber || 'No source number' }}</small>
          </td>
          <td>
            <strong>{{ routeLabel(row) }}</strong>
            <small>{{ row.originLatitude ?? '-' }}, {{ row.originLongitude ?? '-' }} -> {{ row.destinationLatitude ?? '-' }}, {{ row.destinationLongitude ?? '-' }}</small>
          </td>
          <td>{{ needBy(row) }}</td>
          <td>
            <strong>{{ lineSummary(row) }}</strong>
            <small>{{ firstLineItem(row) }}</small>
          </td>
          <td>
            <span>{{ row.validationMessage || 'Ready to create' }}</span>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="7" class="empty-cell">Upload a workbook or document to preview order release rows.</td></tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  @if (result) {
    <app-page-data-panel title="Created Releases" subtitle="Generated through the standard release engine." [resultText]="(result.createdCount || 0) + ' created / ' + (result.failedCount || 0) + ' failed'">
      <div class="created-grid">
        @for (release of result.releases || []; track release.releaseId) {
          <a class="created-card" [routerLink]="['/transport-planning/releases', release.releaseId]">
            <strong>{{ release.releaseNumber }}</strong>
            <small>{{ release.sourceNumber || release.sourceId }}</small>
            <p-tag [value]="release.releaseStatus || 'READY'" severity="success"></p-tag>
          </a>
        }
      </div>
      @if ((result.failedRows || []).length) {
        <div class="failed-summary">
          <strong>Failed rows</strong>
          <span>{{ result.failedRows?.length }} row(s) failed during creation. Keep them in the preview, correct the source, and retry.</span>
        </div>
      }
    </app-page-data-panel>
  }
</app-page-shell>
